import { useCallback, useState } from 'react';
import type { AgentRun, Conversation, CsvImportJob, Message, WhatsappSession } from '@supporchat/shared';
import { useSession } from './hooks/useSession';
import { useCalls } from './hooks/useCalls';
import { useMessageAlerts } from './hooks/useMessageAlerts';
import { Avatar } from './components/Avatar';
import { CallBar } from './components/CallBar';
import { useRealtime } from './hooks/useRealtime';
import { CallsPage } from './pages/CallsPage';
import { InboxPage } from './pages/InboxPage';
import { MonitorPage } from './pages/MonitorPage';
import { TicketsPage } from './pages/TicketsPage';
import { KnowledgePage } from './pages/KnowledgePage';
import { ConnectionPage } from './pages/ConnectionPage';
import { AgentPage } from './pages/AgentPage';
import { WebhooksPage } from './pages/WebhooksPage';

type Tab = 'inbox' | 'monitor' | 'tickets' | 'calls' | 'knowledge' | 'agent' | 'connection' | 'integration';

/**
 * `adminOnly` esconde a aba de quem receberia 403 em toda chamada dela: as rotas
 * de integração exigem administrador porque o corpo de uma entrega carrega a
 * conversa do cliente e o reenvio cria atendimento no outro sistema.
 */
const TABS: Array<{ id: Tab; label: string; adminOnly?: boolean }> = [
  { id: 'inbox', label: 'Atendimentos' },
  { id: 'monitor', label: 'Monitor' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'calls', label: 'Ligações' },
  { id: 'knowledge', label: 'Base de conhecimento' },
  { id: 'agent', label: 'Agente' },
  { id: 'connection', label: 'Conexão' },
  { id: 'integration', label: 'Integração', adminOnly: true },
];

export function App() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>('inbox');

  // Eventos de tempo real são recebidos aqui e distribuídos por assinatura,
  // para que uma só conexão sirva todas as telas.
  const [conversationEvent, setConversationEvent] = useState<Conversation | null>(null);
  const [messageEvent, setMessageEvent] = useState<{ conversationId: string; message: Message } | null>(null);
  /**
   * Ticket que deixou de existir.
   *
   * Carrega o instante junto do id porque o mesmo ticket não volta, mas duas
   * exclusões seguidas precisam ser dois eventos: sem isso, uma tela que já
   * tratou este id ignoraria a segunda notificação.
   */
  const [deletedEvent, setDeletedEvent] = useState<{ conversationId: string; at: number } | null>(null);
  const [sessionEvent, setSessionEvent] = useState<WhatsappSession | null>(null);
  const [qrEvent, setQrEvent] = useState<{ sessionId: string; qrCode: string } | null>(null);
  const [importEvent, setImportEvent] = useState<CsvImportJob | null>(null);
  const [agentEvent, setAgentEvent] = useState<AgentRun | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  // A conversa aberta na tela: mensagem dela com a aba em foco não gera aviso.
  const [openConversationId, setOpenConversationId] = useState<string | null>(null);
  /**
   * Pedido de "abra este atendimento", vindo de outra tela.
   *
   * Carrega o instante junto do id porque o mesmo card pode ser clicado duas
   * vezes seguidas: só o id não mudaria, e a segunda vez não abriria nada.
   */
  const [focusRequest, setFocusRequest] = useState<{ conversationId: string; at: number } | null>(null);
  /**
   * Foto própria trocada nesta sessão.
   *
   * A versão original vem no token, que não é reemitido quando a foto muda — sem
   * este estado, o atendente troca a foto e o topo do painel continua mostrando a
   * antiga até ele recarregar a página. `null` dentro do objeto é "removi a minha
   * foto", diferente de `null` no lugar do objeto, que é "não mexi nela".
   */
  const [avatarTrocado, setAvatarTrocado] = useState<{ version: string | null } | null>(null);

  const openConversation = useCallback((conversationId: string) => {
    setTab('inbox');
    setFocusRequest({ conversationId, at: Date.now() });
  }, []);

  const alerts = useMessageAlerts({ messageEvent, openConversationId });

  const token = session.status === 'ready' ? session.session.token : null;
  // Perfil somente leitura não atende nem origina chamadas.
  const canUseCalls = session.status === 'ready' && session.session.user.role !== 'viewer';
  const calls = useCalls(canUseCalls);

  useRealtime(token, {
    'message.new': useCallback((payload: { conversationId: string; message: Message }) => {
      setMessageEvent(payload);
    }, []),
    // Mesma rota do message.new: a InboxPage substitui a mensagem quando já
    // conhece o id, então áudio transcrito atualiza a bolha existente.
    'message.updated': useCallback((payload: { conversationId: string; message: Message }) => {
      setMessageEvent(payload);
    }, []),
    'conversation.updated': useCallback((payload: { conversation: Conversation }) => {
      setConversationEvent(payload.conversation);
    }, []),
    // Excluído por alguém: quem estiver com o card ou a conversa aberta precisa
    // tirá-lo da tela, senão o painel segue oferecendo um atendimento que a API
    // já não conhece.
    'conversation.deleted': useCallback((payload: { conversationId: string; ticketNumber: number }) => {
      setDeletedEvent({ conversationId: payload.conversationId, at: Date.now() });
    }, []),
    'whatsapp.status': useCallback((payload: { session: WhatsappSession }) => {
      setSessionEvent(payload.session);
    }, []),
    'whatsapp.qr': useCallback((payload: { sessionId: string; qrCode: string }) => {
      setQrEvent(payload);
    }, []),
    'import.progress': useCallback((payload: { job: CsvImportJob }) => {
      setImportEvent(payload.job);
    }, []),
    // O monitor mostra o que o assistente decidiu em cada conversa.
    'agent.run': useCallback((payload: { run: AgentRun }) => {
      setAgentEvent(payload.run);
    }, []),
  });

  if (session.status === 'loading') {
    return (
      <div className="center-screen">
        <div className="center-screen__box muted">Validando acesso...</div>
      </div>
    );
  }

  if (session.status === 'error') {
    return (
      <div className="center-screen">
        <div className="center-screen__box">
          <h2 style={{ marginTop: 0 }}>Não foi possível abrir o painel</h2>
          <p className="muted">{session.message}</p>
        </div>
      </div>
    );
  }

  const { user, tenant } = session.session;
  const isAdmin = user.role === 'admin';
  const avatarVersion = avatarTrocado ? avatarTrocado.version : user.avatarUpdatedAt;
  const tabs = TABS.filter((item) => !item.adminOnly || isAdmin);

  return (
    <div className="app">
      <header className="topbar">
        <span className="topbar__brand">Atendimento</span>
        <span className="topbar__user">{tenant.name}</span>

        <nav className="tabs" role="tablist">
          {tabs.map((item) => (
            <button
              key={item.id}
              className="tab"
              role="tab"
              aria-selected={tab === item.id}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              {item.id === 'inbox' && pendingCount + alerts.unseen > 0 && (
                <span className="tab__badge">{pendingCount + alerts.unseen}</span>
              )}
            </button>
          ))}
        </nav>

        <CallBar calls={calls} />

        <AlertsIndicator alerts={alerts} />

        <span className="topbar__user topbar__me">
          <Avatar kind="user" id={user.id} name={user.name} version={avatarVersion} size="sm" />
          {user.name}
          {user.role !== 'admin' && ` (${user.role === 'viewer' ? 'leitura' : 'atendente'})`}
        </span>
      </header>

      <main className="main">
        {tab === 'inbox' && (
          <InboxPage
            currentUserId={user.id}
            currentUserName={user.name}
            currentUserRole={user.role}
            canWrite={user.role !== 'viewer'}
            canDelete={user.canDeleteTickets}
            calls={calls}
            conversationEvent={conversationEvent}
            messageEvent={messageEvent}
            deletedEvent={deletedEvent}
            focusRequest={focusRequest}
            onPendingCountChange={setPendingCount}
            onOpenConversationChange={(id) => {
              setOpenConversationId(id);
              if (id) alerts.clear();
            }}
          />
        )}
        {tab === 'monitor' && (
          <MonitorPage
            conversationEvent={conversationEvent}
            messageEvent={messageEvent}
            deletedEvent={deletedEvent}
            agentEvent={agentEvent}
            onOpenConversation={openConversation}
          />
        )}
        {tab === 'tickets' && (
          <TicketsPage
            currentUserId={user.id}
            canWrite={user.role !== 'viewer'}
            canDelete={user.canDeleteTickets}
            conversationEvent={conversationEvent}
            deletedEvent={deletedEvent}
            onOpenConversation={openConversation}
          />
        )}
        {tab === 'calls' && <CallsPage onOpenConversation={openConversation} />}
        {tab === 'knowledge' && <KnowledgePage canWrite={user.role !== 'viewer'} importEvent={importEvent} />}
        {tab === 'agent' && (
          <AgentPage
            isAdmin={isAdmin}
            userId={user.id}
            userName={user.name}
            avatarVersion={avatarVersion}
            onAvatarChange={(version) => setAvatarTrocado({ version })}
          />
        )}
        {tab === 'connection' && (
          <ConnectionPage isAdmin={isAdmin} sessionEvent={sessionEvent} qrEvent={qrEvent} />
        )}
        {/* A aba nem aparece para quem não é admin; a checagem repetida evita que
            um estado antigo de `tab` renderize a tela para quem perdeu o papel. */}
        {tab === 'integration' && isAdmin && <WebhooksPage />}
      </main>
    </div>
  );
}

/**
 * Estado dos avisos, no topo do painel.
 *
 * Existe porque as duas formas de aviso podem estar silenciosamente desligadas:
 * o som até o primeiro clique na página, e a notificação até a permissão ser
 * concedida. Sem mostrar isso, o atendente acha que está sendo avisado quando
 * não está.
 */
function AlertsIndicator({ alerts }: { alerts: ReturnType<typeof useMessageAlerts> }) {
  if (alerts.notifications === 'granted' && alerts.audioReady) {
    return (
      <span className="topbar__user" title="Som e notificações ativos">
        🔔 avisos ativos
      </span>
    );
  }

  if (alerts.notifications === 'denied') {
    return (
      <span className="topbar__user" title="As notificações foram bloqueadas nas permissões do navegador para este site">
        🔕 notificações bloqueadas
      </span>
    );
  }

  if (alerts.notifications === 'default') {
    return (
      <button className="btn btn--sm" onClick={() => void alerts.enableNotifications()}>
        Ativar notificações
      </button>
    );
  }

  // Permissão concedida mas o áudio ainda travado pela política de autoplay:
  // qualquer clique na página resolve.
  return (
    <span className="topbar__user" title="Clique em qualquer lugar da página para liberar o som">
      🔔 som após o primeiro clique
    </span>
  );
}
